import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { BarChart3, Users, Activity, GraduationCap, BookOpen, MessageSquareText, FileText, TrendingUp } from "lucide-react";

const PLAN_LABELS = { einzel: "Einzel", team: "Team" };

function fmtLastSeen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff <= 0) return "heute";
  if (diff === 1) return "gestern";
  if (diff < 7) return `vor ${diff} Tagen`;
  return d.toLocaleDateString("de-CH");
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl glass px-5 py-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4 text-gold" />
        <span className="text-xs uppercase tracking-widest">{label}</span>
      </div>
      <div className="font-heading font-bold text-3xl text-gold mt-2">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminStats() {
  const { user } = useAuth();
  const statsQ = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
  });

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const d = statsQ.data;
  const trend = d?.trend || [];
  const maxCount = Math.max(1, ...trend.map((t) => t.count));
  const participants = d?.participants || [];

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 className="h-6 w-6 text-gold" />
          <h1 className="font-heading font-extrabold text-3xl">Kursnutzung</h1>
        </div>
        <p className="text-muted-foreground mb-8 max-w-2xl">
          Überblick, wie viele Teilnehmende den Kurs nutzen, wie weit sie fortgeschritten sind und wie stark der KI-Coach
          und der Briefing-Generator genutzt werden.
        </p>

        {statsQ.isLoading ? (
          <div className="rounded-2xl glass px-5 py-10 text-center text-muted-foreground">Lädt…</div>
        ) : statsQ.isError ? (
          <div className="rounded-2xl glass px-5 py-10 text-center text-muted-foreground" data-testid="stats-error">
            Statistik konnte nicht geladen werden.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <StatCard icon={Users} label="Teilnehmer" value={d.students.total} sub="registrierte Zugänge" testid="stat-total" />
              <StatCard icon={Activity} label="Aktiv heute" value={d.students.active_today} testid="stat-active-today" />
              <StatCard icon={Activity} label="Aktiv 7 Tage" value={d.students.active_7d} testid="stat-active-7" />
              <StatCard icon={Activity} label="Aktiv 30 Tage" value={d.students.active_30d} testid="stat-active-30" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <StatCard icon={TrendingUp} label="Ø Fortschritt" value={`${d.progress.avg_percent}%`} sub="pro Teilnehmer" testid="stat-avg" />
              <StatCard icon={BookOpen} label="Lektionen erledigt" value={d.progress.lessons_done_total} sub="über alle Teilnehmer" testid="stat-lessons" />
              <StatCard icon={GraduationCap} label="Kurs abgeschlossen" value={d.progress.completed_course} sub={`von ${d.students.total}`} testid="stat-completed" />
              <StatCard icon={GraduationCap} label="Module fertig" value={d.progress.modules_complete_total} sub="kumuliert" testid="stat-modules" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <div className="rounded-2xl glass px-5 py-4" data-testid="stat-coach">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MessageSquareText className="h-4 w-4 text-gold" />
                  <span className="text-xs uppercase tracking-widest">KI-Coach Fragen</span>
                </div>
                <div className="flex items-end gap-3 mt-2">
                  <span className="font-heading font-bold text-3xl text-gold">{d.ai.coach_total}</span>
                  <span className="text-sm text-muted-foreground mb-1">gesamt · {d.ai.coach_today} heute</span>
                </div>
              </div>
              <div className="rounded-2xl glass px-5 py-4" data-testid="stat-briefing">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="h-4 w-4 text-gold" />
                  <span className="text-xs uppercase tracking-widest">Briefings generiert</span>
                </div>
                <div className="flex items-end gap-3 mt-2">
                  <span className="font-heading font-bold text-3xl text-gold">{d.ai.briefing_total}</span>
                  <span className="text-sm text-muted-foreground mb-1">gesamt · {d.ai.briefing_today} heute</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl glass-strong p-6 mb-8" data-testid="stat-trend">
              <h2 className="font-heading font-bold text-lg mb-1">Aktive Teilnehmer pro Tag</h2>
              <p className="text-xs text-muted-foreground mb-5">Letzte 14 Tage</p>
              <div className="flex items-end gap-1.5 h-32">
                {trend.map((t) => (
                  <div key={t.date} className="flex-1 flex flex-col items-center justify-end gap-1 h-full" title={`${t.date}: ${t.count}`}>
                    <span className="text-[10px] text-foreground/70">{t.count > 0 ? t.count : ""}</span>
                    <div
                      className="w-full rounded-t-md bg-gradient-to-t from-gold/40 to-gold transition-all"
                      style={{ height: `${Math.max(t.count > 0 ? 6 : 0, (t.count / maxCount) * 100)}%` }}
                    />
                    <span className="text-[9px] text-muted-foreground">{t.date.slice(8)}.{t.date.slice(5, 7)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl glass overflow-hidden" data-testid="participant-table">
              <div className="px-5 py-3 border-b hairline flex items-center justify-between">
                <h2 className="font-heading font-bold text-lg">Teilnehmer</h2>
                <span className="text-xs text-muted-foreground">{participants.length} gesamt</span>
              </div>
              <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground border-b hairline">
                <div className="col-span-4 sm:col-span-3">Name</div>
                <div className="hidden sm:block col-span-3">E-Mail</div>
                <div className="col-span-2 sm:col-span-1">Tarif</div>
                <div className="col-span-3 sm:col-span-3">Fortschritt</div>
                <div className="col-span-3 sm:col-span-2 text-right">Zuletzt aktiv</div>
              </div>
              {participants.length === 0 ? (
                <div className="px-5 py-8 text-center text-muted-foreground" data-testid="participant-empty">
                  Noch keine Teilnehmer registriert.
                </div>
              ) : (
                participants.map((p) => (
                  <div
                    key={p.email}
                    data-testid={`participant-row-${p.email}`}
                    className="grid grid-cols-12 px-5 py-3 items-center border-b hairline last:border-0 text-sm"
                  >
                    <div className="col-span-4 sm:col-span-3 font-medium truncate">{p.name || "—"}</div>
                    <div className="hidden sm:block col-span-3 text-muted-foreground truncate">{p.email}</div>
                    <div className="col-span-2 sm:col-span-1 text-xs text-foreground/80">{PLAN_LABELS[p.plan] || "Einzel"}</div>
                    <div className="col-span-3 sm:col-span-3 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full bg-gold rounded-full" style={{ width: `${p.overall_percent}%` }} />
                        </div>
                        <span className="text-xs text-foreground/80 w-9 text-right">{p.overall_percent}%</span>
                      </div>
                    </div>
                    <div className="col-span-3 sm:col-span-2 text-right text-xs text-muted-foreground">{fmtLastSeen(p.last_seen)}</div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
